<template>
    <div ref="formItemRef" class="s-form-item" :class="formItemClass" :data-prop="prop">
        <!-- 标签 -->
        <label v-if="label || $slots.label" class="s-form-item__label" :style="labelStyle">
            <slot name="label">
                <span v-if="isRequired && showAsterisk" class="s-form-item__asterisk">*</span>
                {{ label }}
            </slot>
        </label>

        <!-- 内容区域 -->
        <div class="s-form-item__content">
            <slot />

            <!-- 错误信息 -->
            <Transition name="s-form-item-error">
                <div v-if="showMessage && currentError" class="s-form-item__error">
                    {{ currentError }}
                </div>
            </Transition>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, inject, onMounted, onUnmounted, ref, useSlots, watch } from 'vue'
    import type { FormItemContext, FormItemExpose, FormItemProps, FormRule } from './form.types'
    import { FORM_CONTEXT_KEY } from './form.types'

    defineOptions({ name: 's-form-item' })

    const props = withDefaults(defineProps<FormItemProps>(), {
        showMessage: true,
    })

    const slots = useSlots()

    // 注入表单上下文
    const formContext = inject(FORM_CONTEXT_KEY, null)

    // 元素引用
    const formItemRef = ref<HTMLDivElement>()

    // 内部错误状态
    const innerError = ref('')

    // 是否已触发校验
    const validateTriggered = ref(false)

    // 初始值
    const initialValue = ref<any>(undefined)

    // 当前错误信息（优先使用外部传入的 error）
    const currentError = computed(() => {
        if (props.error) return props.error
        if (props.prop && formContext) {
            return formContext.getFieldError(props.prop)
        }
        return innerError.value
    })

    // 是否显示必填星号
    const showAsterisk = computed(() => {
        return formContext?.showRequiredAsterisk.value ?? true
    })

    // 获取字段规则
    const fieldRules = computed((): FormRule[] => {
        // 优先使用 FormItem 的规则
        if (props.rules) {
            return Array.isArray(props.rules) ? props.rules : [props.rules]
        }
        // 否则使用 Form 的规则
        if (props.prop && formContext?.rules.value) {
            const rules = formContext.rules.value[props.prop]
            if (rules) {
                return Array.isArray(rules) ? rules : [rules]
            }
        }
        return []
    })

    // 是否必填
    const isRequired = computed(() => {
        if (props.required) return true
        return fieldRules.value.some(rule => rule.required)
    })

    // 标签宽度
    const labelStyle = computed(() => {
        const width = props.labelWidth ?? formContext?.labelWidth.value
        if (!width) return {}
        return {
            width: typeof width === 'number' ? `${width}px` : width,
            flexShrink: 0,
        }
    })

    // 样式类
    const formItemClass = computed(() => ({
        's-form-item--error': !!currentError.value,
        's-form-item--required': isRequired.value,
        's-form-item--no-label': !props.label && !slots.label,
    }))

    /**
     * 校验字段
     */
    async function validate(): Promise<{ valid: boolean; message: string }> {
        validateTriggered.value = true

        if (!props.prop) {
            return { valid: true, message: '' }
        }

        // 使用表单上下文的校验方法
        if (formContext) {
            return await formContext.validateField(props.prop)
        }

        return { valid: true, message: '' }
    }

    /**
     * 重置字段
     */
    function resetField(): void {
        validateTriggered.value = false
        innerError.value = ''

        // 重置值到初始值
        if (props.prop && formContext && initialValue.value !== undefined) {
            formContext.model.value[props.prop] = initialValue.value
        }

        // 清除表单上下文中的错误
        if (props.prop && formContext) {
            formContext.clearFieldError(props.prop)
        }
    }

    /**
     * 清除校验状态
     */
    function clearValidate(): void {
        validateTriggered.value = false
        innerError.value = ''

        if (props.prop && formContext) {
            formContext.clearFieldError(props.prop)
        }
    }

    /**
     * 监听字段值变化，触发 change 校验
     */
    function setupValueWatcher(): void {
        if (!props.prop || !formContext) return

        // 将 prop 赋值给局部常量，确保 TypeScript 类型收窄
        const propKey = props.prop

        // 保存初始值
        initialValue.value = formContext.model.value[propKey]

        // 监听值变化
        watch(
            () => formContext.model.value[propKey],
            () => {
                // 只有触发过校验后才进行实时校验
                const hasChangeTrigger = fieldRules.value.some(rule => rule.trigger === 'change' || !rule.trigger)
                if (validateTriggered.value && hasChangeTrigger) {
                    validate()
                }
            }
        )
    }

    // 注册到表单
    onMounted(() => {
        if (props.prop && formContext) {
            const context: FormItemContext = {
                prop: props.prop,
                validate,
                resetField,
                clearValidate,
            }
            formContext.registerField(context)
            setupValueWatcher()
        }
    })

    // 从表单注销
    onUnmounted(() => {
        if (props.prop && formContext) {
            formContext.unregisterField(props.prop)
        }
    })

    // 暴露方法
    defineExpose<FormItemExpose>({
        validate,
        resetField,
        clearValidate,
    })
</script>

<style scoped lang="scss">
    .s-form-item {
        display: flex;
        margin-bottom: 22px;

        &--no-label {
            .s-form-item__content {
                margin-left: 0;
            }
        }
    }

    .s-form-item__label {
        display: inline-flex;
        align-items: center;
        height: 48px;
        padding-right: 12px;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        color: var(--color-text, #1a1a1a);
    }

    .s-form-item__asterisk {
        margin-right: 4px;
        color: var(--color-price, #f43b3b);
    }

    .s-form-item__content {
        position: relative;
        flex: 1;
        min-width: 0;
    }

    .s-form-item__error {
        position: absolute;
        top: 100%;
        left: 0;
        margin-top: 4px;
        font-size: 12px;
        line-height: 1;
        color: var(--color-price, #f43b3b);
    }

    // 错误信息过渡动画
    .s-form-item-error-enter-active,
    .s-form-item-error-leave-active {
        transition: all 0.2s ease;
    }

    .s-form-item-error-enter-from,
    .s-form-item-error-leave-to {
        opacity: 0;
        transform: translateY(-4px);
    }
</style>
